import React from "react";
import type { ResolvedRange } from "./spec";

interface RangePickerProps {
  value: ResolvedRange;
  onChange: (range: ResolvedRange) => void;
  className?: string;
}

interface Preset {
  id: string;
  label: string;
  /** Window duration in ms; undefined = live. */
  ms?: number;
}

const PRESETS: Preset[] = [
  { id: "live", label: "Live" },
  { id: "1h", label: "1 h", ms: 60 * 60 * 1000 },
  { id: "6h", label: "6 h", ms: 6 * 60 * 60 * 1000 },
  { id: "1d", label: "1 day", ms: 24 * 60 * 60 * 1000 },
  { id: "3d", label: "3 days", ms: 3 * 24 * 60 * 60 * 1000 },
  { id: "1w", label: "1 week", ms: 7 * 24 * 60 * 60 * 1000 },
];

/** Which preset best matches the current range (for active-button styling). */
function activeId(range: ResolvedRange): string {
  if (range.kind === "live") return "live";
  const span = range.endMs - range.startMs;
  let best = PRESETS[1];
  for (const p of PRESETS) {
    if (p.ms && Math.abs(p.ms - span) < Math.abs((best.ms ?? 0) - span))
      best = p;
  }
  return best.id;
}

/** Compact preset bar producing a {@link ResolvedRange}. */
const RangePicker: React.FC<RangePickerProps> = ({
  value,
  onChange,
  className,
}) => {
  const active = activeId(value);
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ""}`}>
      {PRESETS.map((p) => (
        <button
          key={p.id}
          onClick={() =>
            onChange(
              p.ms === undefined
                ? { kind: "live" }
                : {
                    kind: "window",
                    startMs: Date.now() - p.ms,
                    endMs: Date.now(),
                  },
            )
          }
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
            active === p.id
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
};

export default RangePicker;
